import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Card,
  CardContent,
  InputAdornment
} from '@mui/material';
import {
  Calculate,
  TrendingDown,
  ArrowForward,
  Savings
} from '@mui/icons-material';

const insuranceTypes = [
  { value: 'health', label: 'Health Insurance' },
  { value: 'auto', label: 'Car Insurance' },
  { value: 'life', label: 'Life Insurance' },
  { value: 'travel', label: 'Travel Insurance' },
  { value: 'home', label: 'Home Insurance' },
  { value: 'business', label: 'Business Insurance' }
];

const coverageTypes = [
  { value: 'basic', label: 'Basic Coverage' },
  { value: 'standard', label: 'Standard Coverage' },
  { value: 'comprehensive', label: 'Comprehensive Coverage' }
];

const SavingsCalculator = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    current_premium: '',
    product_type: 'health',
    coverage_type: 'standard'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    if (result) setResult(null);
  };

  const handleCalculate = async () => {
    const premium = parseFloat(formData.current_premium);
    if (!formData.current_premium || isNaN(premium) || premium <= 0) {
      setError('Please enter a valid premium amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/products/calculate-savings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_premium: premium,
          product_type: formData.product_type,
          coverage_type: formData.coverage_type
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to calculate savings');
      }

      const data = await response.json();
      setResult(data.data ?? data);
    } catch (err) {
      console.error('Calculator error:', err);
      setError(err.message || 'Failed to calculate savings');
    } finally {
      setLoading(false);
    }
  };

  const handleShowPlan = () => {
    if (result?.best_product?.id) {
      navigate(`/product/${result.best_product.id}`, {
        state: { product: result.best_product }
      });
    }
  };

  const handleFindBestMatch = () => {
    const premium = parseFloat(formData.current_premium);
    navigate(`/insurance/${formData.product_type}`, {
      state: {
        prefilled: {
          is_switching: true,
          current_premium: Number.isFinite(premium) ? premium : undefined,
          coverage_level: formData.coverage_type
        }
      }
    });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Calculate sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" gutterBottom>
          💰 Insurance Savings Calculator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Find out how much you could save with Tutamen
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Calculate Your Savings
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Current Annual Premium"
            type="number"
            value={formData.current_premium}
            onChange={(e) => handleChange('current_premium', e.target.value)}
            placeholder="e.g., 1200"
            helperText="How much are you paying per year?"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>$</Typography>
                </InputAdornment>
              )
            }}
          />

          <FormControl fullWidth>
            <InputLabel>Insurance Type</InputLabel>
            <Select
              value={formData.product_type}
              label="Insurance Type"
              onChange={(e) => handleChange('product_type', e.target.value)}
            >
              {insuranceTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Coverage Type</InputLabel>
            <Select
              value={formData.coverage_type}
              label="Coverage Type"
              onChange={(e) => handleChange('coverage_type', e.target.value)}
            >
              {coverageTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Calculate />}
            onClick={handleCalculate}
            disabled={loading || !formData.current_premium}
            fullWidth
          >
            {loading ? 'Calculating...' : 'Calculate My Savings'}
          </Button>
        </Stack>
      </Paper>

      {result && (
        <Paper elevation={3} sx={{ p: 4, bgcolor: 'success.light' }}>
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center' }}>
              <TrendingDown sx={{ fontSize: 60, color: 'success.dark', mb: 1 }} />
              <Typography variant="h3" color="success.dark" gutterBottom>
                YOU COULD SAVE
              </Typography>
              <Typography variant="h2" fontWeight="bold" color="success.dark">
                ${Number(result.total_benefit ?? 0).toLocaleString()}/year
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom color="success.dark">
                Here's the breakdown:
              </Typography>

              <Stack spacing={2}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Your current plan
                      </Typography>
                      <Typography variant="h6">
                        ${Number(result.current_premium ?? 0).toLocaleString()}/year
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Market average
                      </Typography>
                      <Typography variant="body1">
                        ${Number(result.market_average ?? 0).toLocaleString()}/year
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body2">Best available plan</Typography>
                        {result.best_product?.product_name && (
                          <Typography variant="caption" display="block">
                            {result.best_product.product_name}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="h6" fontWeight="bold">
                        ${Number(result.best_available ?? 0).toLocaleString()}/year
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">Price difference:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.dark">
                    ${Number(result.savings ?? 0).toLocaleString()}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1">+ Tutamen cashback (5%):</Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.dark">
                    ${Number(result.cashback ?? 0).toLocaleString()}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6">Total benefit:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.dark">
                    ${Number(result.total_benefit ?? 0).toLocaleString()}/year
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Stack direction="row" spacing={2} flexWrap="wrap">
              {result.best_product?.id && (
                <Button
                  variant="outlined"
                  onClick={handleShowPlan}
                  sx={{ color: 'success.dark', borderColor: 'success.dark' }}
                >
                  View This Plan
                </Button>
              )}
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleFindBestMatch}
                fullWidth
                sx={{ bgcolor: 'success.dark', flex: 1, minWidth: 200 }}
              >
                Find My Perfect Match
              </Button>
            </Stack>

            <Alert severity="info" icon={<Savings />}>
              <Typography variant="body2">
                <strong>Lebanese families</strong> save an average of <strong>$380/year</strong> by
                switching through Tutamen. Your personalized savings may vary based on coverage needs.
              </Typography>
            </Alert>
          </Stack>
        </Paper>
      )}

      {!result && (
        <Paper elevation={1} sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Why calculate with Tutamen?
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="primary">
                ✓ Transparent Comparison
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We compare 50+ insurers to find you the best deal
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="primary">
                ✓ Cashback Rewards
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get 5-20% cashback on your premiums based on your tier
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="primary">
                ✓ Annual Re-Optimization
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We automatically check for better deals every year
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="primary">
                ✓ No Hidden Fees
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Same price as buying direct, plus our benefits
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}
    </Container>
  );
};

export default SavingsCalculator;
